import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isNonEmptyArray } from "../../../common/utils";

/**
 * Builds a filter payload object for API requests
 * @param {Object} params - Filter parameters
 * @returns {Object} Normalized filter payload
 */
export const buildFiltersPayload = ({
	searchTerm,
	categoriesFilter,
	categoryIds,
	phasesFilter,
	phaseIds,
	ownerIds,
}) => {
	const payload = {
		categories: {
			is: (categoriesFilter?.is ?? categoryIds.length > 0) || false,
			matchAll: categoriesFilter?.matchAll ?? false,
			elements: categoryIds,
		},
		phases: {
			is: (phasesFilter?.is ?? phaseIds.length > 0) || false,
			matchAll: phasesFilter?.matchAll ?? false,
			elements: phaseIds,
		},
		owners: {
			is: ownerIds.length > 0,
			matchAll: false,
			elements: ownerIds,
		},
	};

	if (searchTerm && searchTerm.length > 0) {
		payload.name = searchTerm;
	}

	return payload;
};

/**
 * Extracts owner IDs from various filter formats
 * @param {Array|Object} ownersFilter - Owners filter in various formats
 * @returns {Array} Normalized array of owner IDs
 */
export const extractOwnerIds = (ownersFilter) => {
	if (!ownersFilter) {
		return [];
	}
	if (Array.isArray(ownersFilter)) {
		return ownersFilter;
	}
	if (Array.isArray(ownersFilter?.elements)) {
		return ownersFilter.elements;
	}
	return [];
};

/**
 * Hook for managing deliverables filters state and derived values
 * @returns {Object} Filter state and handlers
 */
const useDeliverablesFilters = () => {
	const location = useLocation();
	const [filters, setFilters] = useState({});
	const isMountedRef = useRef(true);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Handle filters from navigation state
	useEffect(() => {
		if (location?.state?.filters && isMountedRef.current) {
			const incoming = location.state.filters || {};
			const normalized = { ...incoming };
			const navPhases = incoming.phases;
			if (navPhases) {
				const elements = Array.isArray(navPhases?.elements) ? navPhases.elements : navPhases?.PHASE;
				normalized.phases = {
					PHASE: Array.isArray(elements) ? elements : [],
					is: typeof navPhases.is === "boolean" ? navPhases.is : isNonEmptyArray(elements),
					matchAll: typeof navPhases.matchAll === "boolean" ? navPhases.matchAll : false,
				};
			}
			setFilters(normalized);
		}
	}, [location.state]);

	const searchTerm = useMemo(() => (filters?.content || "").trim(), [filters]);
	const phasesFilter = useMemo(() => filters?.phases || {}, [filters]);
	const phaseIds = useMemo(() => phasesFilter?.elements ?? phasesFilter?.PHASE ?? [], [phasesFilter]);
	const categoriesFilter = useMemo(() => filters?.categories || {}, [filters]);
	const categoryIds = useMemo(
		() => categoriesFilter?.CATEGORY ?? categoriesFilter?.elements ?? [],
		[categoriesFilter]
	);
	const ownersFilter = filters?.owners;
	const ownerIds = useMemo(() => extractOwnerIds(ownersFilter), [ownersFilter]);

	const handleApplyFilters = useCallback((newFilters) => {
		const next = newFilters || {};
		setFilters(next);
	}, []);

	const filtersPayload = useMemo(
		() =>
			buildFiltersPayload({
				searchTerm,
				categoriesFilter,
				categoryIds,
				phasesFilter,
				phaseIds,
				ownerIds,
			}),
		[searchTerm, categoriesFilter, categoryIds, phasesFilter, phaseIds, ownerIds]
	);

	return {
		filters,
		setFilters,
		searchTerm,
		phasesFilter,
		phaseIds,
		categoriesFilter,
		categoryIds,
		ownerIds,
		handleApplyFilters,
		filtersPayload,
	};
};

export default useDeliverablesFilters;
